import type { IPairing } from "@vmosolutions/oc-interfaces";
import { Grid } from "@mui/material";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import type { IPairingEditorEventGroup } from "../PairingEventTable";
import { DateDisplay } from "src/components/DateDisplay";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { mergeDraft } from "../PairingEditorWindow";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import type { IRootState } from "src/redux/reducers/CombinedReducers";

type IDutyPeriodSummary = {
  id: string;
  eventGroupIndex: number;
  eventGroup: IPairingEditorEventGroup;
  displayAsUTC: boolean;
};

// maxTime allowed for checking before or after after (used for first and last eventgroups);
// Set to 8 hours in milliseconds.
const maxTime = 28800000;

export const DutyPeriodCheckout = (props: IDutyPeriodSummary): JSX.Element => {
  const { id, eventGroupIndex, eventGroup, displayAsUTC } = props;
  const dispatch = useDispatch();
  const storedPairing = useSelector((state: IRootState): IPairing | undefined => state.crewSchedule.pairingEditors[id]);

  if ((eventGroup.sortedEvents?.length ?? 0) > 0 && storedPairing) {
    const { eventGroups, checkInOutOverrides } = storedPairing;

    // It is possible for both of these to be true (i.e one event group);
    const isLast = eventGroupIndex === eventGroups.length - 1;

    // CHECKOUT TIME to display.
    const lastEvent = eventGroup.sortedEvents?.slice(-1).pop();
    const lastEventEndMs = lastEvent?.end?.valueOf();
    const endICAO = lastEvent?.endICAO;
    const tzICAO = !displayAsUTC ? endICAO : undefined;

    const checkOutOverride = checkInOutOverrides?.find((o) => o.eventGroup === eventGroupIndex && !o.inout);

    const checkOutTime = (checkOutOverride?.time ?? eventGroup.end).valueOf();
    const minCheckOut = lastEventEndMs;
    // Get next eventgroup that contains flights or non flying assignments. (skip rests)
    //! Should be safe to jump foward 2 eventgroups if the one after contains a rest.
    const nextEventIndex =
      eventGroups[eventGroupIndex + 1]?.restPeriods.length === 0 ? eventGroupIndex + 1 : eventGroupIndex + 2;
    const nextEventWithFlights = eventGroups[nextEventIndex];
    const maxCheckOut =
      isLast || nextEventIndex >= eventGroups.length
        ? (lastEventEndMs ?? 0) + maxTime
        : nextEventWithFlights.start.valueOf();
    const checkOutWarning =
      minCheckOut != null && checkOutTime <= maxCheckOut && checkOutTime >= minCheckOut
        ? null
        : "Check-Out out of acceptable bounds or colliding with other events in the pairing.";

    const handleCheckOverride = async (newState: boolean, inout: boolean): Promise<void> => {
      const updatedCheckInOutOverrides = [...(checkInOutOverrides ?? [])];
      if (newState) {
        // ADD
        updatedCheckInOutOverrides.push({
          inout: inout,
          time: inout ? eventGroup.start.valueOf() : eventGroup.end.valueOf(), // store current eventgroup checkin as starting time.
          eventGroup: eventGroupIndex,
        });
      } else {
        // REMOVE
        const overrideIndex = updatedCheckInOutOverrides.findIndex(
          (o) => o.eventGroup === eventGroupIndex && o.inout === inout
        );
        updatedCheckInOutOverrides.splice(overrideIndex, 1);
      }

      // If override is turned off, run draftPairing to re-apply default checkin time.
      if (!newState) {
        const draft = await mergeDraft({ ...storedPairing, checkInOutOverrides: updatedCheckInOutOverrides }, dispatch);
        // A failed draft keeps the override, as removing it alone would leave the overridden time in place.
        if (draft) {
          dispatch(updatePairingEditor(id, draft.pairing));
        }
      } else {
        dispatch(
          updatePairingEditor(id, {
            checkInOutOverrides: updatedCheckInOutOverrides,
          })
        );
      }
    };

    // Change value of assigned override.
    const handleChangeCheckTime = (value: number, inout: boolean): void => {
      const updatedCheckInOutOverrides = _.cloneDeep(checkInOutOverrides); // use deep clone as to not mutate.
      const overrideIndex = updatedCheckInOutOverrides?.findIndex(
        (o) => o.eventGroup === eventGroupIndex && o.inout === inout
      );
      if (overrideIndex != null && overrideIndex > -1 && updatedCheckInOutOverrides != null) {
        updatedCheckInOutOverrides[overrideIndex].time = value;
        dispatch(
          updatePairingEditor(id, {
            checkInOutOverrides: updatedCheckInOutOverrides,
          })
        );
      }
    };

    const checkoutDuration =
      lastEventEndMs != null && checkOutTime - lastEventEndMs > 0
        ? ` (${convertMilli2DaysHrsMin(checkOutTime - lastEventEndMs)})`
        : " (No Check-Out)";

    return (
      <Grid container className="duty_period_summary">
        <Grid container className="duty_row checkout" alignItems="center">
          <span style={{ fontWeight: 500 }}>Check-Out: </span>
          <CheckboxField
            className="field checkboxfield"
            label="Override"
            name={`eventgroup_${eventGroupIndex}_checkout`}
            checked={!!checkOutOverride}
            onChange={_.debounce(() => {
              void handleCheckOverride(!checkOutOverride, false);
            }, 500)}
          />
          {checkOutOverride ? (
            <DateTimeField
              label=""
              className="field datetimefield"
              name={`eventgroup_${eventGroupIndex}_checkout`}
              value={checkOutTime}
              onChange={(value: string | number) => handleChangeCheckTime(Number(value), false)}
              isEpoch={true}
              showAirportTime={endICAO}
              localizeDisplay={!displayAsUTC}
              hideLocalizeBtn={true}
            />
          ) : (
            <DateDisplay date={checkOutTime} tzICAO={tzICAO} />
          )}
          {checkoutDuration}
        </Grid>

        {checkOutOverride && checkOutWarning && (
          <Grid item>
            <span className="override_warning">{checkOutWarning}</span>
          </Grid>
        )}
      </Grid>
    );
  }
  return <></>;
};
